import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useRef, useState, type DragEvent, type ReactNode } from 'react';
import type {
  CierreDelMes,
  FilaDelContraste,
  GrupoOtif,
  IncidenciaDesempeno,
  IndicadoresOtif,
  LineaCascada,
  ReporteOtifCargado,
  ResultadoCargaOtif,
} from '@manejo-alcanza/contracts';
import {
  anularReporteOtif,
  cantidad,
  cargarReportesOtif,
  cierreDelMes,
  contrastarOtif,
  detalleDelIndicador,
  fecha,
  pesos,
  porcentaje,
  resolverIncidenciaDesempeno,
} from '../api';
import { Aviso, Boton, Card, Cargando, Error_, Tabla, Tile } from '../componentes/basicos';

/**
 * F8 — el cierre de mes.
 *
 * **No es un tablero de adorno.** Responde tres preguntas, en este orden:
 *
 * 1. ¿Cómo vamos este mes?
 * 2. ¿Dónde se está perdiendo?
 * 3. ¿Coincide con lo que dice el cliente?
 *
 * Y una cuarta, debajo: ¿cuánto quedó del mes? — la cascada del P&L, **con los
 * huecos donde están**.
 *
 * ## Lo que esta pantalla no hace
 *
 * **No enseña un porcentaje sin decir sobre cuánto está calculado.** Hoy hay 42
 * órdenes cargadas y muy pocas recepciones registradas; un «OTIF 0 %» se vería
 * perfectamente creíble y sería mentira. Cuando no hay con qué calcular, dice
 * «no se sabe» y cuántas líneas faltan por registrar.
 *
 * **Y no rellena la cascada.** Las líneas que dependen de costos y gastos salen
 * en blanco, con la frase de qué falta. Un cero ahí daría una ganancia bruta
 * igual a los ingresos netos.
 *
 * Es una pantalla de consulta: se mira sentado. Pero **tiene que caber en un
 * teléfono**, así que las tablas van en `Tabla`, que se desplaza sola y avisa de
 * que se desplaza, y los puntos de corte van hacia arriba con `sm:`.
 */
export function Cierre(): ReactNode {
  const [periodo, setPeriodo] = useState<string | undefined>(undefined);
  const cliente = useQueryClient();

  const cierre = useQuery({
    queryKey: ['cierre', periodo ?? 'ultimo'],
    queryFn: () => cierreDelMes(periodo),
  });

  if (cierre.isLoading) return <Cargando que="el cierre del mes" />;
  if (cierre.isError) return <Error_>{(cierre.error as Error).message}</Error_>;
  if (!cierre.data) return null;

  const datos = cierre.data;
  const refrescar = () => void cliente.invalidateQueries({ queryKey: ['cierre'] });

  return (
    <div className="space-y-6">
      <header className="flex flex-wrap items-end justify-between gap-3">
        <div>
          <h1 className="text-2xl font-semibold">Cierre de mes</h1>
          <p className="mt-1 text-tenue">
            Cuánto se entregó completo y a tiempo, y qué quedó del mes.
          </p>
        </div>
        <SelectorDePeriodo
          periodo={datos.periodo}
          disponibles={datos.periodosConDatos}
          alCambiar={setPeriodo}
        />
      </header>

      <ComoVamos indicadores={datos.propio} periodo={datos.periodo} />
      <Tendencia puntos={datos.tendencia} actual={datos.periodo} />
      <DondeSePierde porProducto={datos.porProducto} porCliente={datos.porCliente} periodo={datos.periodo} />
      <ContraElCliente
        periodo={datos.periodo}
        reportes={datos.reportes}
        contraste={datos.contraste}
        incidencias={datos.incidencias}
        alCambiar={refrescar}
      />
      <Cascada
        lineas={datos.cascada}
        fuera={datos.fueraDeLaCascada}
        hastaDondeLlega={datos.hastaDondeLlega}
      />
    </div>
  );
}

function SelectorDePeriodo({
  periodo,
  disponibles,
  alCambiar,
}: {
  periodo: string;
  disponibles: string[];
  alCambiar: (p: string) => void;
}): ReactNode {
  return (
    <label className="text-sm">
      <span className="mr-2 text-tenue">Mes</span>
      <select
        value={periodo}
        onChange={(e) => alCambiar(e.target.value)}
        className="min-h-[44px] rounded-md border border-borde bg-superficie px-2 sm:min-h-0 sm:py-1.5"
      >
        {/* Si el mes que se está mirando no está en la lista, se añade: no
            puede desaparecer del selector el que está en pantalla. */}
        {(disponibles.includes(periodo) ? disponibles : [...disponibles, periodo])
          .slice()
          .reverse()
          .map((p) => (
            <option key={p} value={p}>
              {nombreDeMes(p)}
            </option>
          ))}
      </select>
    </label>
  );
}

// ---------------------------------------------------------------------------
// 1. ¿Cómo vamos este mes?
// ---------------------------------------------------------------------------

function ComoVamos({
  indicadores,
  periodo,
}: {
  indicadores: IndicadoresOtif;
  periodo: string;
}): ReactNode {
  const sinMedir = indicadores.lineasSinRecepcion;
  const nadaMedido = indicadores.lineasMedidas === 0;

  return (
    <section className="space-y-3">
      <h2 className="text-base font-semibold">¿Cómo vamos este mes?</h2>

      {/*
        El aviso va ARRIBA de las cifras, no debajo.
        Un porcentaje calculado sobre la mitad de las líneas no es falso, pero
        tampoco es lo que parece, y quien lo lee tiene que saberlo antes de
        leerlo. Las cifras agregadas solo suman lo comparable (docs/DISENO.md).
      */}
      {sinMedir > 0 && (
        <Aviso>
          {nadaMedido ? (
            <>
              <strong>Todavía no hay nada medido de este mes.</strong> {sinMedir}{' '}
              {sinMedir === 1 ? 'línea está' : 'líneas están'} sin recepción registrada, así que no
              hay OTIF que calcular. No es un 0 %: es que nadie ha anotado aún qué llegó.
            </>
          ) : (
            <>
              Calculado sobre <strong>{indicadores.lineasMedidas}</strong>{' '}
              {indicadores.lineasMedidas === 1 ? 'línea' : 'líneas'}.{' '}
              <strong>{sinMedir}</strong> {sinMedir === 1 ? 'queda' : 'quedan'} fuera por no tener
              recepción registrada: ni suman ni restan, faltan.
            </>
          )}
        </Aviso>
      )}

      <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-4">
        <Tile
          etiqueta="OTIF"
          valor={porcentaje(indicadores.otif)}
          pie="Completo y a tiempo, sobre lo pedido"
        />
        <Tile
          etiqueta="Infull"
          valor={porcentaje(indicadores.infull)}
          pie={`${cantidad(indicadores.unidadesRecibidas)} de ${cantidad(indicadores.unidadesPedidas)} unidades`}
        />
        <Tile
          etiqueta="Ontime"
          valor={porcentaje(indicadores.ontime)}
          pie={`${cantidad(indicadores.unidadesOntime)} llegaron dentro de la fecha`}
        />
        <Tile
          etiqueta="No facturado por faltante"
          valor={pesos(indicadores.valorNoFacturado)}
          pie={`De ${pesos(indicadores.valorPedido)} pedidos`}
        />
      </div>

      {indicadores.lineasEnCero > 0 && (
        <p className="text-sm text-alerta">
          <strong>{indicadores.lineasEnCero}</strong>{' '}
          {indicadores.lineasEnCero === 1 ? 'línea se entregó' : 'líneas se entregaron'} en cero.
        </p>
      )}

      <AbrirHastaElDato periodo={periodo} />
    </section>
  );
}

/**
 * Las líneas detrás de la cifra.
 *
 * *Una gráfica que no se puede auditar no se usa: se desconfía de ella.* Va
 * plegada porque son cincuenta filas y la pregunta de arriba se responde con
 * cuatro números; pero está a un clic, siempre.
 */
function AbrirHastaElDato({
  periodo,
  presentacionId,
}: {
  periodo: string;
  presentacionId?: string;
}): ReactNode {
  const [abierto, setAbierto] = useState(false);
  const detalle = useQuery({
    queryKey: ['cierre-detalle', periodo, presentacionId ?? 'todo'],
    queryFn: () => detalleDelIndicador(periodo, presentacionId),
    enabled: abierto,
  });

  return (
    <details
      className="rounded-lg border border-borde bg-superficie p-3"
      onToggle={(e) => setAbierto((e.target as HTMLDetailsElement).open)}
    >
      <summary className="cursor-pointer text-sm font-medium">
        Ver las líneas que componen esta cifra
      </summary>

      {detalle.isLoading && abierto && <p className="mt-2 text-sm text-tenue">Cargando…</p>}
      {detalle.data && (
        <div className="mt-3">
          <Tabla>
            <thead>
              <tr className="border-b border-borde text-left text-xs text-tenue">
                <th className="p-2">Orden</th>
                <th className="p-2">Producto</th>
                <th className="p-2 text-right">Pedida</th>
                <th className="p-2 text-right">Recibida</th>
                <th className="p-2">Pactada</th>
                <th className="p-2">Recibió</th>
                <th className="p-2">A tiempo</th>
              </tr>
            </thead>
            <tbody>
              {detalle.data.lineas.map((l) => (
                <tr key={l.lineaId} className="border-b border-borde last:border-0">
                  <td className="p-2 font-medium">{l.numeroOrden}</td>
                  <td className="p-2">{l.producto}</td>
                  <td className="cifra p-2">{cantidad(l.cantidadPedida)}</td>
                  {/*
                    Vacío y cero no se pintan igual. «—» es «nadie lo ha
                    registrado»; un 0 en rojo es «vino el camión y no recibieron
                    nada». Son dos hechos distintos y el segundo es reclamable.
                  */}
                  <td
                    className={`cifra p-2 ${
                      l.cantidadRecibida !== null && Number(l.cantidadRecibida) < Number(l.cantidadPedida)
                        ? 'text-alerta'
                        : ''
                    }`}
                  >
                    {l.cantidadRecibida === null ? (
                      <span className="text-tenue italic">sin registrar</span>
                    ) : (
                      cantidad(l.cantidadRecibida)
                    )}
                  </td>
                  <td className="p-2 text-xs">{fecha(l.fechaEntregaMax)}</td>
                  <td className="p-2 text-xs">
                    {l.fechaRecepcion ? fecha(l.fechaRecepcion) : '—'}
                  </td>
                  <td className="p-2 text-xs">
                    {l.cantidadRecibida === null ? '—' : l.aTiempo ? 'sí' : <span className="text-alerta">no</span>}
                  </td>
                </tr>
              ))}
            </tbody>
          </Tabla>
        </div>
      )}
    </details>
  );
}

/**
 * La tendencia propia: doce meses.
 *
 * **Sin meta, y eso es a propósito** *(Pedro, 21/09/2026)*: no hay ninguna
 * pactada con el cliente y dibujar una inventada convertiría una línea de
 * referencia en un compromiso que nadie adquirió. La comparación es contra los
 * meses anteriores y contra el reporte del cliente.
 */
function Tendencia({ puntos, actual }: { puntos: CierreDelMes['tendencia']; actual: string }): ReactNode {
  const conDatos = puntos.filter((p) => p.indicadores.otif !== null);
  if (conDatos.length === 0) return null;

  const maximo = Math.max(...conDatos.map((p) => Number(p.indicadores.otif)), 1);

  return (
    <Card titulo="Los últimos doce meses">
      <div className="flex items-end gap-1 overflow-x-auto pb-1">
        {puntos.map((p) => {
          const valor = p.indicadores.otif === null ? null : Number(p.indicadores.otif);
          return (
            <div key={p.periodo} className="flex min-w-[2.5rem] flex-1 flex-col items-center gap-1">
              <div className="flex h-24 w-full items-end">
                {valor === null ? (
                  /* Un mes sin datos se deja vacío. Una barra a cero diría que
                     ese mes se entregó nada, y lo que pasa es que no se sabe. */
                  <div className="h-full w-full rounded-t border border-dashed border-borde" />
                ) : (
                  <div
                    className={`w-full rounded-t ${valor < 0.8 ? 'bg-alerta' : 'bg-acento'}`}
                    style={{ height: `${Math.max((valor / maximo) * 100, 2)}%` }}
                  />
                )}
              </div>
              <span
                className={`text-[0.65rem] ${p.periodo === actual ? 'font-semibold' : 'text-tenue'}`}
              >
                {p.periodo.slice(4)}/{p.periodo.slice(2, 4)}
              </span>
            </div>
          );
        })}
      </div>
      <p className="mt-2 text-xs text-tenue">
        OTIF por mes. Las barras punteadas son meses sin nada medido —no son un cero—. No hay meta
        dibujada: no hay ninguna pactada.
      </p>
    </Card>
  );
}

// ---------------------------------------------------------------------------
// 2. ¿Dónde se está perdiendo?
// ---------------------------------------------------------------------------

function DondeSePierde({
  porProducto,
  porCliente,
  periodo,
}: {
  porProducto: GrupoOtif[];
  porCliente: GrupoOtif[];
  periodo: string;
}): ReactNode {
  if (porProducto.length === 0) return null;

  return (
    <section className="space-y-3">
      <h2 className="text-base font-semibold">¿Dónde se está perdiendo?</h2>
      <p className="text-sm text-tenue">
        En el reporte real del Éxito el faltante pesa mucho más que el retraso: 58 % de infull
        contra 91 % de ontime. <strong>Eso no es un problema de software</strong> — el software
        solo lo pone a la vista.
      </p>

      <div className="grid gap-4 lg:grid-cols-2">
        <TablaDeGrupos titulo="Por producto" grupos={porProducto} periodo={periodo} abrible />
        <TablaDeGrupos titulo="Por cliente" grupos={porCliente} periodo={periodo} />
      </div>
    </section>
  );
}

function TablaDeGrupos({
  titulo,
  grupos,
  periodo,
  abrible = false,
}: {
  titulo: string;
  grupos: GrupoOtif[];
  periodo: string;
  abrible?: boolean;
}): ReactNode {
  const [abierto, setAbierto] = useState<string | null>(null);

  return (
    <Card titulo={titulo}>
      <Tabla>
        <thead>
          <tr className="border-b border-borde text-left text-xs text-tenue">
            <th className="p-2">{titulo === 'Por producto' ? 'Producto' : 'Cliente'}</th>
            <th className="p-2 text-right">Pedidas</th>
            <th className="p-2 text-right">Recibidas</th>
            <th className="p-2 text-right">Infull</th>
            <th className="p-2 text-right">OTIF</th>
            <th className="p-2 text-right">No facturado</th>
          </tr>
        </thead>
        <tbody>
          {grupos.map((g) => (
            <tr
              key={g.clave}
              onClick={abrible ? () => setAbierto(abierto === g.clave ? null : g.clave) : undefined}
              className={`border-b border-borde last:border-0 ${abrible ? 'cursor-pointer hover:bg-fondo' : ''}`}
            >
              <td className="p-2">{g.nombre}</td>
              <td className="cifra p-2">{cantidad(g.indicadores.unidadesPedidas)}</td>
              <td className="cifra p-2">{cantidad(g.indicadores.unidadesRecibidas)}</td>
              <td className="cifra p-2">{porcentaje(g.indicadores.infull)}</td>
              <td
                className={`cifra p-2 ${
                  g.indicadores.otif !== null && Number(g.indicadores.otif) < 0.8 ? 'text-alerta' : ''
                }`}
              >
                {porcentaje(g.indicadores.otif)}
              </td>
              <td className="cifra p-2">{pesos(g.indicadores.valorNoFacturado)}</td>
            </tr>
          ))}
        </tbody>
      </Tabla>

      {abierto && (
        <div className="mt-3">
          <AbrirHastaElDato periodo={periodo} presentacionId={abierto} />
        </div>
      )}
    </Card>
  );
}

// ---------------------------------------------------------------------------
// 3. ¿Coincide con lo que dice el cliente?
// ---------------------------------------------------------------------------

function ContraElCliente({
  periodo,
  reportes,
  contraste,
  incidencias,
  alCambiar,
}: {
  periodo: string;
  reportes: ReporteOtifCargado[];
  contraste: FilaDelContraste[];
  incidencias: IncidenciaDesempeno[];
  alCambiar: () => void;
}): ReactNode {
  const abiertas = incidencias.filter((i) => !i.resuelta);

  return (
    <section className="space-y-3">
      <h2 className="text-base font-semibold">¿Coincide con lo que dice el cliente?</h2>

      <CargarReporte periodo={periodo} alCargar={alCambiar} />

      {reportes.length > 0 && (
        <Card titulo="Reportes cargados">
          <ul className="space-y-2 text-sm">
            {reportes.map((r) => (
              <ReporteCargado key={r.id} reporte={r} alAnular={alCambiar} />
            ))}
          </ul>
        </Card>
      )}

      {contraste.length > 0 && (
        <Card titulo="Lo nuestro contra lo suyo">
          <Tabla>
            <thead>
              <tr className="border-b border-borde text-left text-xs text-tenue">
                <th className="p-2">Producto</th>
                <th className="p-2 text-right">Pedidas (ellos)</th>
                <th className="p-2 text-right">Pedidas (nosotros)</th>
                <th className="p-2 text-right">Recibidas (ellos)</th>
                <th className="p-2 text-right">Recibidas (nosotros)</th>
                <th className="p-2">¿Cuadra?</th>
              </tr>
            </thead>
            <tbody>
              {contraste.map((f) => (
                <tr key={`${f.periodo}-${f.pluCliente}`} className="border-b border-borde last:border-0">
                  <td className="p-2">
                    {f.nombre ?? (
                      <span className="text-tenue">
                        <span className="font-mono text-xs">{f.pluCliente}</span>
                        <span className="ml-1 text-xs italic">no identificado</span>
                      </span>
                    )}
                    {f.descripcionCliente && (
                      <span className="block text-xs text-tenue">{f.descripcionCliente}</span>
                    )}
                  </td>
                  <td className="cifra p-2">{cantidad(f.cliente.unidadesPedidas)}</td>
                  <td className="cifra p-2">
                    {f.nuestro ? cantidad(f.nuestro.unidadesPedidas) : '—'}
                  </td>
                  <td className="cifra p-2">{cantidad(f.cliente.unidadesRecibidas)}</td>
                  <td className="cifra p-2">
                    {f.nuestro ? cantidad(f.nuestro.unidadesRecibidas) : '—'}
                  </td>
                  <td className="p-2 text-xs">
                    {f.codigo === null ? (
                      <span className="text-acento">sí</span>
                    ) : (
                      <span className="text-alerta">{textoDelCodigo(f.codigo)}</span>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </Tabla>
          <p className="mt-2 text-xs text-tenue">
            El cruce va <strong>por mes y por producto</strong>: los identificadores del reporte no
            son los de la orden de compra, y no se inventa una equivalencia.
          </p>
        </Card>
      )}

      {abiertas.length > 0 && (
        <Card titulo={`Lo que hay que mirar (${abiertas.length})`}>
          <p className="mb-3 text-sm text-tenue">
            Una diferencia no se resuelve sola y no se ajusta a lo que diga el cliente.{' '}
            <strong>Se mira y se decide</strong>, y queda dicho qué se decidió.
          </p>
          <ul className="space-y-3">
            {abiertas.map((i) => (
              <Incidencia key={i.id} incidencia={i} alResolver={alCambiar} />
            ))}
          </ul>
        </Card>
      )}

      {reportes.length > 0 && (
        <VolverAContrastar periodo={periodo} alContrastar={alCambiar} />
      )}
    </section>
  );
}

function CargarReporte({ periodo, alCargar }: { periodo: string; alCargar: () => void }): ReactNode {
  const [arrastrando, setArrastrando] = useState(false);
  const entrada = useRef<HTMLInputElement>(null);

  const carga = useMutation({
    mutationFn: (archivos: File[]) => cargarReportesOtif(archivos),
    onSuccess: alCargar,
  });

  function soltar(e: DragEvent<HTMLDivElement>): void {
    e.preventDefault();
    setArrastrando(false);
    const archivos = [...e.dataTransfer.files];
    if (archivos.length > 0) carga.mutate(archivos);
  }

  return (
    <div className="space-y-3">
      <div
        onDragOver={(e) => {
          e.preventDefault();
          setArrastrando(true);
        }}
        onDragLeave={() => setArrastrando(false)}
        onDrop={soltar}
        onClick={() => entrada.current?.click()}
        className={`cursor-pointer rounded-xl border-2 border-dashed p-8 text-center transition ${
          arrastrando ? 'border-acento bg-acento-tenue' : 'border-borde bg-superficie'
        }`}
      >
        <p className="font-medium">Suelta aquí el reporte OTIF del cliente</p>
        <p className="mt-1 text-sm text-tenue">
          El .xlsx que manda el Éxito, tal como llega. Varios a la vez.
        </p>
        <input
          ref={entrada}
          type="file"
          multiple
          accept=".xlsx,application/vnd.openxmlformats-officedocument.spreadsheetml.sheet"
          className="hidden"
          onChange={(e) => {
            const archivos = [...(e.target.files ?? [])];
            if (archivos.length > 0) carga.mutate(archivos);
            e.target.value = '';
          }}
        />
      </div>

      {carga.isPending && <Aviso>Leyendo el reporte…</Aviso>}
      {carga.isError && <Error_>{(carga.error as Error).message}</Error_>}
      {carga.data && <ResultadoDeLaCarga resultados={carga.data.archivos} periodo={periodo} />}
    </div>
  );
}

/** Resultado **por archivo**, igual que en la carga de órdenes. */
function ResultadoDeLaCarga({
  resultados,
  periodo,
}: {
  resultados: ResultadoCargaOtif[];
  periodo: string;
}): ReactNode {
  return (
    <ul className="space-y-2 text-sm">
      {resultados.map((r, i) => (
        <li key={i} className="rounded-md border border-borde bg-superficie px-3 py-2">
          <span className="font-mono text-xs">{r.nombreArchivo}</span>
          {r.estado === 'CARGADO' && (
            <span className="ml-2 text-acento">
              cargado · {r.lineas} líneas · {nombreDeMes(r.periodo ?? periodo)}
            </span>
          )}
          {r.estado === 'YA_EXISTIA' && <span className="ml-2 text-tenue">{r.mensaje}</span>}
          {r.estado === 'RECHAZADO' && <span className="ml-2 text-alerta">{r.mensaje}</span>}
        </li>
      ))}
    </ul>
  );
}

function ReporteCargado({
  reporte,
  alAnular,
}: {
  reporte: ReporteOtifCargado;
  alAnular: () => void;
}): ReactNode {
  const [motivo, setMotivo] = useState('');
  const [pidiendoMotivo, setPidiendoMotivo] = useState(false);

  const anular = useMutation({
    mutationFn: () => anularReporteOtif(reporte.id, motivo),
    onSuccess: () => {
      setPidiendoMotivo(false);
      setMotivo('');
      alAnular();
    },
  });

  /*
   * El resumen impreso contra lo que sumamos de sus propias líneas.
   *
   * Cuando no coinciden, el archivo trae la tabla dinámica sin refrescar. Es el
   * mismo contraste que el total impreso de un PDF de orden contra la suma de
   * sus líneas, y se dice en vez de callarse.
   */
  const resumenCuadra =
    reporte.resumen === null ||
    (reporte.resumen.unidadesPedidas === reporte.sumado.unidadesPedidas &&
      reporte.resumen.unidadesRecibidas === reporte.sumado.unidadesRecibidas &&
      reporte.resumen.unidadesOntime === reporte.sumado.unidadesOntime);

  return (
    <li className="rounded-md border border-borde px-3 py-2">
      <div className="flex flex-wrap items-baseline justify-between gap-2">
        <span>
          <strong>{reporte.clienteNombre}</strong> · {nombreDeMes(reporte.periodo)} ·{' '}
          {reporte.lineas} líneas
          {reporte.anulado && <span className="ml-2 text-tenue italic">anulado</span>}
        </span>
        {!reporte.anulado && (
          <Boton tipo="secundario" onClick={() => setPidiendoMotivo(!pidiendoMotivo)}>
            Anular
          </Boton>
        )}
      </div>

      {reporte.lineasSinProducto > 0 && (
        <p className="mt-1 text-xs text-aviso">
          {reporte.lineasSinProducto} líneas traen un PLU que no está en el maestro. No se adivinó
          cuál era: quedan levantadas abajo.
        </p>
      )}

      {!resumenCuadra && (
        <p className="mt-1 text-xs text-alerta">
          El resumen impreso del archivo dice {cantidad(reporte.resumen?.unidadesPedidas ?? null)}{' '}
          pedidas y sus propias líneas suman {cantidad(reporte.sumado.unidadesPedidas)}. Puede venir
          con la tabla dinámica sin refrescar.
        </p>
      )}

      {pidiendoMotivo && (
        <div className="mt-2 space-y-2">
          <p className="text-xs text-tenue">
            No se borra: queda con su motivo, y el mes vuelve a quedar libre para cargar el
            corregido.
          </p>
          <input
            value={motivo}
            onChange={(e) => setMotivo(e.target.value)}
            placeholder="¿Por qué se anula?"
            className="min-h-[44px] w-full rounded-md border border-borde px-2 text-sm sm:min-h-0 sm:py-1.5"
          />
          <Boton
            tipo="peligro"
            deshabilitado={motivo.trim().length < 3 || anular.isPending}
            onClick={() => anular.mutate()}
          >
            Anular el reporte
          </Boton>
          {anular.isError && <Error_>{(anular.error as Error).message}</Error_>}
        </div>
      )}
    </li>
  );
}

function Incidencia({
  incidencia,
  alResolver,
}: {
  incidencia: IncidenciaDesempeno;
  alResolver: () => void;
}): ReactNode {
  const [nota, setNota] = useState('');

  const resolver = useMutation({
    mutationFn: () => resolverIncidenciaDesempeno(incidencia.id, nota),
    onSuccess: alResolver,
  });

  return (
    <li className="rounded-md border border-alerta/30 bg-red-50 px-3 py-2">
      <div className="text-sm font-medium text-alerta">{textoDelCodigo(incidencia.codigo)}</div>
      <div className="text-sm">
        {/*
          Sin producto identificado se enseña el PLU crudo, que es lo único que
          hay. Cuando el sistema no sabe algo, lo dice: el dato tal cual y la
          palabra, nunca un espacio en blanco (`docs/DISENO.md`).
        */}
        {incidencia.nombre ?? (
          <span className="text-tenue">
            <span className="font-mono text-xs">{incidencia.pluCliente ?? '—'}</span>
            <span className="ml-1 text-xs italic">no identificado</span>
          </span>
        )}
      </div>
      <pre className="mt-1 overflow-x-auto rounded bg-superficie p-2 text-[0.7rem] text-tenue">
        {JSON.stringify(incidencia.detalle, null, 2)}
      </pre>

      <div className="mt-2 flex flex-wrap gap-2">
        <input
          value={nota}
          onChange={(e) => setNota(e.target.value)}
          placeholder="¿Qué se decidió?"
          className="min-h-[44px] flex-1 rounded-md border border-borde px-2 text-sm sm:min-h-0 sm:py-1.5"
        />
        <Boton
          tipo="secundario"
          deshabilitado={nota.trim().length < 3 || resolver.isPending}
          onClick={() => resolver.mutate()}
        >
          Dejar constancia
        </Boton>
      </div>
      {resolver.isError && <Error_>{(resolver.error as Error).message}</Error_>}
    </li>
  );
}

function VolverAContrastar({
  periodo,
  alContrastar,
}: {
  periodo: string;
  alContrastar: () => void;
}): ReactNode {
  const contrastar = useMutation({
    mutationFn: () => contrastarOtif(periodo),
    onSuccess: alContrastar,
  });

  return (
    <div className="flex flex-wrap items-center gap-3">
      <Boton tipo="secundario" deshabilitado={contrastar.isPending} onClick={() => contrastar.mutate()}>
        Volver a contrastar
      </Boton>
      <p className="text-xs text-tenue">
        Un desacuerdo de hoy puede dejar de serlo mañana, cuando alguien registre la recepción que
        faltaba.
      </p>
    </div>
  );
}

// ---------------------------------------------------------------------------
// 4. ¿Cuánto quedó del mes?
// ---------------------------------------------------------------------------

function Cascada({
  lineas,
  fuera,
  hastaDondeLlega,
}: {
  lineas: LineaCascada[];
  fuera: CierreDelMes['fueraDeLaCascada'];
  hastaDondeLlega: string;
}): ReactNode {
  return (
    <section className="space-y-3">
      <h2 className="text-base font-semibold">¿Cuánto quedó del mes?</h2>

      {/*
        El aviso va arriba, y dice hasta dónde llega la cascada ANTES de que
        nadie lea un número. Una cascada que se ve completa y termina en un
        subtotal en blanco hace dudar del subtotal; una que declara dónde para,
        no.
      */}
      <Aviso>{hastaDondeLlega}</Aviso>

      <Card>
        <table className="w-full text-sm">
          <tbody>
            {lineas.map((l) => (
              <tr
                key={l.clave}
                className={`border-b border-borde last:border-0 ${
                  l.signo === 'SUBTOTAL' ? 'font-semibold' : ''
                }`}
              >
                <td className="py-2 pr-2">
                  <span className={l.signo === 'MENOS' ? 'pl-3 text-tenue' : ''}>
                    {l.signo === 'MENOS' ? '− ' : ''}
                    {l.etiqueta}
                  </span>
                  {l.porQue && <span className="block text-xs text-tenue">{l.porQue}</span>}
                </td>
                <td className="cifra py-2 text-right">
                  {l.valor === null ? (
                    /*
                      Lo que falta se escribe con palabras, no con un guión.
                      Un «—» se lee como «cero» de reojo, y un cero aquí daría
                      una ganancia bruta igual a los ingresos netos.
                    */
                    <span className="text-xs font-normal text-tenue italic">
                      {l.estado === 'FUERA_DE_ALCANCE' ? 'lo ingresa el contador' : 'falta el dato'}
                    </span>
                  ) : (
                    pesos(l.valor)
                  )}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </Card>

      <Card titulo="Fuera de la cascada, y no por descuido">
        <dl className="space-y-3 text-sm">
          <div className="flex flex-wrap items-baseline justify-between gap-2">
            <dt>
              <strong>Retenciones</strong>
              <span className="block text-xs text-tenue">
                Salen del pago pero <strong>vuelven</strong> en la declaración. No son menor
                ingreso: restarlas haría que el margen pareciera peor de lo que es.
              </span>
            </dt>
            <dd className="cifra">{pesos(fuera.fiscal)}</dd>
          </div>
          <div className="flex flex-wrap items-baseline justify-between gap-2">
            <dt>
              <strong>Deducciones sin explicar</strong>
              <span className="block text-xs text-tenue">
                No se restan: restarlas sería decidir que son un gasto, y lo que no se sabe es
                justamente qué son. <strong>Es lo más reclamable que hay aquí.</strong>
              </span>
            </dt>
            <dd className="cifra">{pesos(fuera.noExplicada)}</dd>
          </div>
        </dl>
      </Card>
    </section>
  );
}

// ---------------------------------------------------------------------------

const MESES = [
  'enero',
  'febrero',
  'marzo',
  'abril',
  'mayo',
  'junio',
  'julio',
  'agosto',
  'septiembre',
  'octubre',
  'noviembre',
  'diciembre',
];

/** `202511` → `noviembre de 2025`. Un AAAAMM no se lee de un vistazo. */
function nombreDeMes(periodo: string): string {
  const mes = MESES[Number(periodo.slice(4, 6)) - 1];
  return mes ? `${mes} de ${periodo.slice(0, 4)}` : periodo;
}

function textoDelCodigo(codigo: FilaDelContraste['codigo']): string {
  switch (codigo) {
    case 'OTIF_DISCREPANCIA':
      return 'No cuadra';
    case 'PLU_NO_RECONOCIDO':
      return 'PLU sin identificar';
    case 'SIN_DATO_PROPIO':
      return 'Nosotros no tenemos nada';
    default:
      return '';
  }
}
